import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { ProductNames } from '@xbs/xbs-enums';
import { RequestObject } from './httpService.types';
import { getAppConfig } from '../../utils/configuration';
import { AuthStateInterface, getAuthInfo, logUserLogout, redirectToLogin } from '../../utils/auth';
import { REFRESH_URL } from '../../constants';
import { getPlatformModule } from '../platform/platform';


class HTTPService {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      // configure this with the base url of the app
    });
    this.client.interceptors.request.use(this.authorizeRequest.bind(this));

    // Skipping proxy for now
    // this.client.interceptors.request.use(this.getProxiedRequest.bind(this));

    this.client.interceptors.response.use((response) => response, this.handleErrorResponse.bind(this));
  }


  public async request<T>({ method, apiUrlKey, relativePath = '', params, data, headers }: RequestObject) {
    const config = getAppConfig();
    const url = `${apiUrlKey && config[apiUrlKey] ? config[apiUrlKey]! : ''}${relativePath}`;
    //ToDo: The product names need to be replaced when we start using this request.
    const xbsHeaders = {
      xbsproductuuid: ProductNames.ByName.Tpiq.Uuid,
      xbsproductid: ProductNames.ByName.Tpiq.Id,
      xbsisnewui: 'true',
      xbsoriginurl: window.location.href ?? ''
    };

    return this.client.request<T>({ method, url, params, data, headers: { ...headers, ...xbsHeaders } });
  }

  public async requestWithoutAuthHeader<T>({ method, apiUrlKey, relativePath = '', params, data, headers }: RequestObject) {
    const config = getAppConfig();
    const newClient = axios.create({});
    newClient.interceptors.request.use(this.getProxiedRequest.bind(this));

    newClient.interceptors.response.use((response) => response, this.handleErrorResponse.bind(this));

    const url = `${apiUrlKey && config[apiUrlKey] ? config[apiUrlKey]! : ''}${relativePath}`;
    //ToDo: The product names need to be replaced when we start using this request.
    const xbsHeaders = {
      xbsproductuuid: ProductNames.ByName.Tpiq.Uuid,
      xbsproductid: ProductNames.ByName.Tpiq.Id,
      xbsisnewui: 'true',
      xbsoriginurl: window.location.href ?? ''
    };

    return newClient.request<T>({ method, data, url, params, headers: { ...headers, ...xbsHeaders } });
  }

  public async refreshToken() {
    const authorizationInfo: AuthStateInterface = getAuthInfo();
    const config =  getAppConfig();
    const authUrl = config.authUrl;
    const { authToken, refreshToken } = authorizationInfo;
    const refreshedAuthResponse = await fetch(`${authUrl}/${REFRESH_URL}`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${String(authToken)}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refreshToken })
    });

    if (!refreshedAuthResponse.ok) {
      await logUserLogout('refresh_token_response_not_ok');
      await redirectToLogin();
      return authorizationInfo;
    }

    const refreshedAuth = await refreshedAuthResponse.json();
    const {
      data: {
        idToken: {
          jwtToken: newAccessToken,
          payload: { exp: newExpiration }
        },
        refreshToken: { token: newRefreshToken }
      }
    } = refreshedAuth;
    return {
      authToken: newAccessToken,
      refreshToken: newRefreshToken,
      expiration: newExpiration * 1000,
      lastRefreshDate: new Date().getDate()
    };
  }

  authorizeRequest(requestConfig: AxiosRequestConfig): AxiosRequestConfig {
    const authorizationInfo: AuthStateInterface = getAuthInfo();
    const { authToken } = authorizationInfo;

    if (!authToken) {
      void logUserLogout('authorize_request_failed');
      void redirectToLogin();
    }

    requestConfig.headers.Authorization = `Bearer ${String(authToken)}`;
    return requestConfig;
  }

  async appendXbsHeaders(requestConfig: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    const { IdentityInfo } = await getPlatformModule();
    const session = await IdentityInfo.getIdentitySession();
    const { containerUuid, roleUuid, userUuid, productUuid } = session;
    //ToDo: The product names need to be replaced when we start using this request.
    requestConfig.headers = {
      ...requestConfig.headers,
      xbscontaineruuid: containerUuid ?? '',
      xbsroleuuid: roleUuid ?? '',
      xbsuseruuid: userUuid ?? '',
      xbsproductuuid: productUuid ? productUuid : ProductNames.ByName.Tpiq.Uuid,
      xbsisnewui: 'true',
      xbsoriginurl: window.location.href ?? ''
    };

    return requestConfig;
  }

  private async getProxiedRequest(requestConfig: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    const { authUrl, proxyUrl } = getAppConfig();
    requestConfig.url = requestConfig.url ?? '';
    const isHttps: boolean = requestConfig.url.startsWith('https://');
    const isAuthStack: boolean = requestConfig.url.includes(authUrl);
    const isS3Call: boolean = requestConfig.url.includes('s3.amazonaws.com');
    const isSelfCall: boolean = requestConfig.url.includes(window.location.host);
    const shouldProxyRequest: boolean = isHttps && !isAuthStack && !isS3Call && !isSelfCall;

    if (shouldProxyRequest) {
      const params = new URLSearchParams(requestConfig.params || {}).toString();
      const url = params ? `${requestConfig.url}?${params}` : requestConfig.url;
      const urlWithProxy = `${proxyUrl}/v1/http?redirectUrl=${encodeURIComponent(url)}`;
      requestConfig.url = urlWithProxy;
      requestConfig = await this.appendXbsHeaders(requestConfig);
    }

    return requestConfig;
  }

  private handleErrorResponse(error: AxiosError): void {
    throw error;
  }
}

export default HTTPService;